// Data Health > Pipeline tab.
//
// Two panels rendered top-to-bottom:
//
//   1. Three layer cards (Bronze / Silver / Gold) - all-time totals + rate + jobs.
//   2. Pipeline Jobs table - last-30h rows + counters.
//
// Fetches the dedicated endpoint and polls every few seconds so the operator
// can watch pipeline state evolve without manual refresh. Layer cards read
// from a scheduled cache server-side; the polling keeps Jobs rows fresh.

import { useCallback, useEffect, useState } from "react";
import { ApiHttpError, type ApiConfig, getDataHealthPipeline } from "../api";
import { formatEntity } from "../format";
import type {
  DataHealthLayerCard,
  DataHealthPipelineResponse,
  DataHealthPipelineRow,
} from "../types";

const POLL_INTERVAL_MS = 5000;
const LAYER_CHILD_PIPELINES = ["gold_layer", "silver_layer", "bronze_layer", "raw_to_bronze"];
const GROUP_PARENT_PIPELINES = new Set(["etl_pipeline"]);
const SYNTHETIC_LAYER_ROWS = [
  { pipeline: "gold_layer", label: "Gold Layer", hop: "Decision-Ready Layer", carriesParentMetrics: false },
  { pipeline: "silver_layer", label: "Silver Layer", hop: "Cleansing Layer", carriesParentMetrics: false },
  { pipeline: "bronze_layer", label: "Bronze Layer", hop: "Raw Ingestion Layer", carriesParentMetrics: true },
];

type JobBlock =
  | { kind: "row"; row: DataHealthPipelineRow }
  | { kind: "group"; parent: DataHealthPipelineRow; children: DataHealthPipelineRow[] };

export function PipelinePanel({ config }: { config: ApiConfig }) {
  const [payload, setPayload] = useState<DataHealthPipelineResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loaded, setLoaded] = useState(false);
  const [collapsedGroups, setCollapsedGroups] = useState<Set<string>>(() => new Set());

  const refresh = useCallback(async () => {
    try {
      const resp = await getDataHealthPipeline(config);
      setPayload(resp);
      setError(null);
    } catch (e) {
      setError(formatError(e));
    } finally {
      setLoaded(true);
    }
  }, [config]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  useEffect(() => {
    const id = window.setInterval(() => {
      void refresh();
    }, POLL_INTERVAL_MS);
    return () => window.clearInterval(id);
  }, [refresh]);

  const toggleGroup = useCallback((key: string) => {
    setCollapsedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }, []);

  if (!loaded && !payload) {
    return <div className="empty">Loading pipeline health...</div>;
  }

  return (
    <div className="pipeline-panel">
      {error && <div className="error-banner">{error}</div>}

      {payload && (
        <>
          <section className="layer-cards" aria-label="Medallion layer cards">
            {payload.data.layers.map((card) => (
              <LayerCard key={card.layer_key} card={card} />
            ))}
          </section>

          <section className="jobs-section" aria-label="Pipeline Jobs">
            <header className="jobs-header">
              <div className="jobs-title">
                <h2>Pipeline Jobs</h2>
                <span className="jobs-window">Last 30h</span>
              </div>
              <Counters counters={payload.data.jobs.counters} />
            </header>

            {payload.data.jobs.rows.length === 0 ? (
              <div className="empty">No pipeline activity in the last 30h.</div>
            ) : (
              <div className="jobs-table" role="table">
                <div className="jobs-row jobs-row-head" role="row">
                  <span role="columnheader">Pipeline / Route</span>
                  <span role="columnheader">Entity</span>
                  <span role="columnheader">Throughput</span>
                  <span role="columnheader">Lag</span>
                  <span role="columnheader">Last Run</span>
                  <span role="columnheader">Status</span>
                </div>
                {buildJobBlocks(payload.data.jobs.rows).map((block) => {
                  if (block.kind === "group") {
                    const groupKey = rowKey(block.parent);
                    const collapsed = collapsedGroups.has(groupKey);
                    return (
                      <JobGroup
                        key={"group@" + groupKey}
                        parent={block.parent}
                        children={block.children}
                        collapsed={collapsed}
                        onToggle={() => toggleGroup(groupKey)}
                      />
                    );
                  }
                  return <JobRow key={rowKey(block.row)} row={block.row} />;
                })}
              </div>
            )}
          </section>
        </>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Layer card
// ---------------------------------------------------------------------------
function LayerCard({ card }: { card: DataHealthLayerCard }) {
  return (
    <div className={"layer-card layer-card-" + card.layer_key + " layer-status-" + card.status}>
      <div className="layer-card-head">
        <div>
          <h3>{card.display_name}</h3>
          <p className="layer-subtitle">{card.subtitle}</p>
        </div>
        <span className={"layer-status-pill layer-status-pill-" + card.status}>
          {layerStatusLabel(card.status)}
        </span>
      </div>
      {card.status_message && (
        <p className={"status-message status-message-" + card.status}>{card.status_message}</p>
      )}
      <dl className="layer-stats">
        <div className="layer-stat">
          <dt>Storage</dt>
          <dd>{formatTotalLine(card)}</dd>
        </div>
        <div className="layer-stat">
          <dt>Rate</dt>
          <dd>{card.rate.label}</dd>
        </div>
        <div className="layer-stat">
          <dt>Jobs</dt>
          <dd>{card.jobs.label}</dd>
        </div>
      </dl>
    </div>
  );
}

function formatTotalLine(card: DataHealthLayerCard): string {
  const rc = card.totals.record_count;
  if (rc == null) return "-";
  return formatCompactInt(rc) + " records";
}

function layerStatusLabel(status: string): string {
  if (status === "healthy") return "Healthy";
  if (status === "warning") return "Warning";
  if (status === "error") return "Error";
  return status;
}

// ---------------------------------------------------------------------------
// Counters
// ---------------------------------------------------------------------------
function Counters({
  counters,
}: {
  counters: { running: number; warning: number; error: number; total: number };
}) {
  return (
    <div className="jobs-counters" aria-label="Pipeline counters">
      <CounterChip kind="running" value={counters.running} />
      <CounterChip kind="warning" value={counters.warning} />
      <CounterChip kind="error" value={counters.error} />
    </div>
  );
}

function CounterChip({ kind, value }: { kind: "running" | "warning" | "error"; value: number }) {
  const label =
    kind === "running" ? "running" : kind === "warning" ? "warning" : "error";
  return (
    <span className={"counter-chip counter-chip-" + kind}>
      <span className="counter-dot" />
      <span className="counter-value">{value}</span>
      <span className="counter-label">{label}</span>
    </span>
  );
}

// ---------------------------------------------------------------------------
// Job row
// ---------------------------------------------------------------------------
function JobGroup({
  parent,
  children,
  collapsed,
  onToggle,
}: {
  parent: DataHealthPipelineRow;
  children: DataHealthPipelineRow[];
  collapsed: boolean;
  onToggle: () => void;
}) {
  return (
    <div className={"jobs-group" + (collapsed ? " jobs-group-collapsed" : "")}>
      <JobRow row={parent} variant="parent" collapsed={collapsed} onToggle={onToggle} />
      {!collapsed && (
        <div className="jobs-group-children">
          {children.map((child, index) => (
            <JobRow
              key={rowKey(child)}
              row={child}
              variant={index === children.length - 1 ? "child-last" : "child"}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function JobRow({
  row,
  variant = "normal",
  collapsed = false,
  onToggle,
}: {
  row: DataHealthPipelineRow;
  variant?: "normal" | "parent" | "child" | "child-last";
  collapsed?: boolean;
  onToggle?: () => void;
}) {
  const variantClass =
    variant === "normal"
      ? ""
      : variant === "parent"
        ? " jobs-row-parent"
        : variant === "child-last"
          ? " jobs-row-child jobs-row-child-last"
          : " jobs-row-child";
  return (
    <div className={"jobs-row jobs-row-status-" + row.status + variantClass} role="row">
      <div className="job-col-route" role="cell">
        <span className="pipeline-title-line">
          {variant === "parent" && (
            <button
              type="button"
              className={"tree-toggle" + (collapsed ? " tree-toggle-collapsed" : "")}
              aria-expanded={!collapsed}
              aria-label={(collapsed ? "Expand " : "Collapse ") + row.pipeline_label}
              onClick={onToggle}
            />
          )}
          <strong className="pipeline-name">{row.pipeline_label}</strong>
        </span>
        <div className="pipeline-route">
          {row.route.map((hop, i) => (
            <span key={i} className="route-hop">
              {i > 0 && <span className="route-arrow">-&gt;</span>}
              <span>{hop}</span>
            </span>
          ))}
        </div>
      </div>
      <div className="job-col-entities" role="cell">
        {row.entities.length === 0 ? (
          <span className="muted">-</span>
        ) : (
          <ul className="entity-list">
            {row.entities.map((e) => (
              <li
                key={e.name}
                title={
                  e.record_count == null
                    ? formatEntity(e.name)
                    : `${e.record_count.toLocaleString()} records`
                }
              >
                <span className="entity-name">{formatEntity(e.name)}</span>
                {e.record_count != null && (
                  <span className="entity-count">{formatCompactInt(e.record_count)}</span>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>
      <div className="job-col-throughput" role="cell">{formatThroughput(row.throughput)}</div>
      <div className="job-col-lag" role="cell">{row.lag.label}</div>
      <div className="job-col-last-run" role="cell">
        <span title={row.last_run.ts_utc ?? ""}>{row.last_run.label}</span>
      </div>
      <div className="job-col-status" role="cell">
        <span className={"status-pill row-status row-status-" + row.status}>{row.status}</span>
        {row.status_message && (
          <span className={"status-message status-message-" + row.status}>
            {row.status_message}
          </span>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------
function buildJobBlocks(rows: DataHealthPipelineRow[]): JobBlock[] {
  const childPipelineOrder = new Map(
    LAYER_CHILD_PIPELINES.map((pipeline, index) => [pipeline, index]),
  );
  const parentSources = new Set(
    rows.filter((row) => GROUP_PARENT_PIPELINES.has(row.pipeline)).map((row) => row.source_system),
  );
  const consumed = new Set<string>();
  const childrenBySource = new Map<string, DataHealthPipelineRow[]>();

  for (const row of rows) {
    if (!childPipelineOrder.has(row.pipeline)) continue;
    const current = childrenBySource.get(row.source_system) ?? [];
    current.push(row);
    childrenBySource.set(row.source_system, current);
  }

  for (const children of childrenBySource.values()) {
    children.sort(
      (a, b) =>
        (childPipelineOrder.get(a.pipeline) ?? 99) -
        (childPipelineOrder.get(b.pipeline) ?? 99),
    );
  }

  const blocks: JobBlock[] = [];
  for (const row of rows) {
    const key = rowKey(row);
    if (consumed.has(key)) continue;
    if (parentSources.has(row.source_system) && childPipelineOrder.has(row.pipeline)) {
      continue;
    }

    if (GROUP_PARENT_PIPELINES.has(row.pipeline)) {
      const actualChildren = childrenBySource.get(row.source_system) ?? [];
      const children =
        row.source_system === "klaviyo"
          ? klaviyoChildrenForParent(row, actualChildren)
          : actualChildren.length > 0
            ? actualChildren
            : syntheticChildrenForParent(row);
      if (children.length > 0) {
        for (const child of children) consumed.add(rowKey(child));
        blocks.push({ kind: "group", parent: parentSummaryRow(row, children.length), children });
        continue;
      }
    }

    blocks.push({ kind: "row", row });
  }
  return blocks;
}

function klaviyoChildrenForParent(
  parent: DataHealthPipelineRow,
  actualChildren: DataHealthPipelineRow[],
): DataHealthPipelineRow[] {
  if (parent.pipeline !== "etl_pipeline") return actualChildren;
  const byLayer = new Map<string, DataHealthPipelineRow>();
  for (const child of actualChildren) {
    byLayer.set(layerSlotForPipeline(child.pipeline), child);
  }
  const sourceHop = parent.route[0] || parent.source_system;
  return SYNTHETIC_LAYER_ROWS.map((layer) => {
    const actual = byLayer.get(layer.pipeline);
    if (actual) return actual;
    return {
      ...parent,
      pipeline: layer.pipeline,
      pipeline_label: layer.label,
      route: [sourceHop, layer.hop],
      entities: layer.carriesParentMetrics ? parent.entities : [],
      throughput: layer.carriesParentMetrics
        ? parent.throughput
        : { rows_per_hour: null, label: "-" },
    };
  });
}

function syntheticChildrenForParent(parent: DataHealthPipelineRow): DataHealthPipelineRow[] {
  if (parent.pipeline !== "etl_pipeline" || parent.source_system !== "klaviyo") return [];
  const sourceHop = parent.route[0] || parent.source_system;
  return SYNTHETIC_LAYER_ROWS.map((layer) => ({
    ...parent,
    pipeline: layer.pipeline,
    pipeline_label: layer.label,
    route: [sourceHop, layer.hop],
    entities: layer.carriesParentMetrics ? parent.entities : [],
    throughput: layer.carriesParentMetrics
      ? parent.throughput
      : { rows_per_hour: null, label: "-" },
  }));
}

function layerSlotForPipeline(pipeline: string): string {
  return pipeline === "raw_to_bronze" ? "bronze_layer" : pipeline;
}

function parentSummaryRow(parent: DataHealthPipelineRow, childCount: number): DataHealthPipelineRow {
  const unit = childCount === 1 ? "route" : "routes";
  return {
    ...parent,
    route: parent.route.length > 0 ? [parent.route[0]] : parent.route,
    entities: [{ name: `${childCount} ${unit}`, record_count: null }],
    throughput: { rows_per_hour: null, label: "-" },
  };
}

function rowKey(row: DataHealthPipelineRow): string {
  return row.pipeline + "@" + row.source_system;
}

function formatCompactInt(value: number): string {
  const rounded = Math.round(value);
  if (rounded >= 1_000_000_000) return Math.round(rounded / 1_000_000_000) + "B";
  if (rounded >= 1_000_000) return Math.round(rounded / 1_000_000) + "M";
  if (rounded >= 1_000) return Math.round(rounded / 1_000) + "K";
  return String(rounded);
}

function formatThroughput(throughput: DataHealthPipelineRow["throughput"]): string {
  if (throughput.rows_per_hour == null) return throughput.label;
  const rounded = Math.round(throughput.rows_per_hour);
  if (rounded >= 1000) return `${formatCompactInt(rounded)} rows/hr`;
  return rounded === 1 ? "1 row/hr" : `${rounded} rows/hr`;
}

function formatError(e: unknown): string {
  if (e instanceof ApiHttpError) {
    return `${e.status} ${e.code}: ${e.message}${e.requestId ? ` (req ${e.requestId})` : ""}`;
  }
  if (e instanceof Error) return e.message;
  return String(e);
}
